import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { customPageAPI } from '../../api';

interface CustomPageData {
  id: number;
  title: string;
  content: string;
  page_url: string;
  status: string;
  view_count: number;
  created_at: string;
  updated_at: string;
  creator_name: string;
  updater_name: string;
}

const CustomPageContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0.5rem 0rem;
`;

const ContentContainer = styled.div`
  width: 100%;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
`;

const PageHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  padding-bottom: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background-color: #f8f9fa;
  padding: 0.5rem 1.5rem;
  border-radius: 8px;
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes.xxl};
  color: #333;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-weight: 700;
`;

const PageMeta = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.secondary};
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const PageMetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  margin-right: 1rem;
  color: #555;
  font-size: 13px;
  
  &:last-child {
    margin-right: 0;
  }
`;

const PageContent = styled.div`
  font-size: 16px;
  line-height: 1.8;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  padding: ${({ theme }) => theme.spacing.md};
  background-color: white;
  border-radius: 8px;
  min-height: 300px;
  
  img {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
    margin: 1rem 0;
  }
  
  p {
    margin-bottom: 1.2rem;
  }
  
  h1, h2, h3, h4, h5, h6 {
    margin-top: 2rem;
    margin-bottom: 1rem;
    font-weight: 600;
    color: #333;
  }
  
  h1 { font-size: 24px; }
  h2 { font-size: 22px; }
  h3 { font-size: 20px; }
  h4 { font-size: 18px; }
  h5 { font-size: 16px; }
  h6 { font-size: 15px; }
  
  ul, ol {
    padding-left: 2rem;
    margin-bottom: 1.2rem;
  }
  
  blockquote {
    border-left: 4px solid ${({ theme }) => theme.colors.primary};
    padding: 1rem 1.5rem;
    font-style: italic;
    margin: 1.5rem 0;
    background-color: #f8f9fa;
    border-radius: 0 4px 4px 0;
  }

  a {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const NotFoundMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
  font-size: 18px;
  background-color: #f8f9fa;
  border-radius: 8px;
  margin-top: 2rem;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
  font-size: 16px;
`;

const CustomPage: React.FC = () => {
  const { page_url } = useParams<{ page_url: string }>();
  const navigate = useNavigate();
  const [customPage, setCustomPage] = useState<CustomPageData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCustomPage = async () => {
      if (!page_url) return;
      
      try {
        setLoading(true);
        const response = await customPageAPI.getCustomPageByUrl(page_url);
        setCustomPage(response.data.data);
        setError(null);
      } catch (err: any) {
        console.error('커스텀 페이지 로딩 오류:', err);
        
        if (err.response && err.response.status === 404) {
          setError('요청하신 페이지를 찾을 수 없습니다.');
        } else {
          setError('페이지를 불러오는 중 오류가 발생했습니다.');
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchCustomPage();
  }, [page_url]);

  if (loading) {
    return (
      <CustomPageContainer>
        <LoadingMessage>페이지를 불러오는 중...</LoadingMessage>
      </CustomPageContainer>
    );
  }

  if (error || !customPage) {
    return (
      <CustomPageContainer>
        <NotFoundMessage>
          {error || '페이지를 찾을 수 없습니다.'}
        </NotFoundMessage>
      </CustomPageContainer>
    );
  }

  return (
    <CustomPageContainer>
      <ContentContainer>
        {/* <PageHeader>
          <PageTitle>{customPage.title}</PageTitle>
          <PageMeta>
            <div>
              <PageMetaItem>작성자: {customPage.creator_name}</PageMetaItem>
              <PageMetaItem>작성일: {new Date(customPage.created_at).toLocaleString()}</PageMetaItem>
            </div>
            <div>
              <PageMetaItem>조회수: {customPage.view_count}</PageMetaItem>
              <PageMetaItem>최종 수정: {new Date(customPage.updated_at).toLocaleString()}</PageMetaItem>
            </div>
          </PageMeta>
        </PageHeader> */}

        <PageContent dangerouslySetInnerHTML={{ __html: customPage.content }} />
      </ContentContainer>
    </CustomPageContainer>
  );
};

export default CustomPage; 